import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, ChevronDown } from 'lucide-react';
import { Modal, ModalActions } from '../../../shared/ui/Modal';
import { Button } from '../../../shared/ui/Button';
import { Input } from '../../../shared/ui/Input';
import { Textarea } from '../../../shared/ui/Textarea';
import { toast } from '../../../shared/ui/toast';
import { useDebouncedValue } from '../../../shared/hooks/useDebouncedValue';
import { ApiError } from '../../../shared/api/http';
import { formatLongDate } from '../../../shared/lib/format';
import { emitOnboardingEvent, registerIntentHandler } from '../../onboarding/intents';
import { useAliasCheck, useAliasSuggestions, useCreateMailbox, useDomains } from '../api';
import { ALIAS_RE } from '../lib';

const DESCRIPTION_MAX = 200;

export function CreateMailboxModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const domainsQuery = useDomains();
  const domains = useMemo(() => domainsQuery.data?.items ?? [], [domainsQuery.data]);

  const [alias, setAlias] = useState('');
  const [domain, setDomain] = useState('');
  const [description, setDescription] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (!domain && domains.length > 0) {
      setDomain(domains.find((d) => d.default)?.name ?? domains[0].name);
    }
  }, [domains, domain]);

  // reset on close
  useEffect(() => {
    if (!open) {
      setAlias('');
      setDescription('');
      setSuggestions([]);
      setTouched(false);
      setPrefilled(false);
    }
  }, [open]);

  // Онбординг не открывает форму сам: он лишь просит владельца состояния сделать это (FR-24).
  useEffect(
    () => registerIntentHandler('mailbox.createModal.open', () => {
      onOpenChange(true);
    }),
    [onOpenChange],
  );

  const debouncedAlias = useDebouncedValue(alias.trim(), 300);
  const regexValid = debouncedAlias === '' || ALIAS_RE.test(debouncedAlias);
  const checkQuery = useAliasCheck(debouncedAlias, domain, open && regexValid);

  const suggestionsMutation = useAliasSuggestions(domain);
  const createMutation = useCreateMailbox();

  const regenerate = () => {
    suggestionsMutation.mutate(3, {
      onSuccess: (data) => {
        setSuggestions(data.suggestions);
      },
    });
  };

  // Правка Ирины §1: адрес предзаполняется сгенерированным именем при открытии формы.
  const { mutate: requestSuggestions } = suggestionsMutation;
  useEffect(() => {
    if (!open || prefilled || !domain) return;
    setPrefilled(true);
    requestSuggestions(1, {
      onSuccess: (data) => {
        const generated = data.suggestions[0];
        // Не перетираем адрес, если пользователь начал печатать до ответа сервера.
        if (generated) setAlias((current) => (current === '' ? generated : current));
      },
    });
  }, [open, prefilled, domain, requestSuggestions]);

  const descriptionTooLong = description.length > DESCRIPTION_MAX;

  let aliasError: string | null = null;
  if (touched && debouncedAlias === '') {
    aliasError = t('mailbox.create.errors.empty');
  } else if (debouncedAlias && !regexValid) {
    aliasError = t('mailbox.create.errors.regex_invalid');
  } else if (checkQuery.data && !checkQuery.data.available) {
    const r = checkQuery.data.reason;
    if (r === 'frozen') {
      aliasError = t('mailbox.create.errors.frozen', {
        date: formatLongDate(checkQuery.data.frozenUntil),
      });
    } else if (r === 'in_trash') {
      aliasError = checkQuery.data.frozenUntil
        ? t('mailbox.create.errors.in_trash_until', {
            date: formatLongDate(checkQuery.data.frozenUntil),
          })
        : t('mailbox.create.errors.in_trash');
    } else if (r === 'already_exists') {
      aliasError = t('mailbox.create.errors.already_exists');
    } else {
      aliasError = t('mailbox.create.errors.regex_invalid');
    }
  }

  const canSubmit =
    debouncedAlias.length >= 3 &&
    regexValid &&
    !aliasError &&
    !descriptionTooLong &&
    !checkQuery.isFetching &&
    !!domain &&
    !createMutation.isPending;

  const onSubmit = () => {
    setTouched(true);
    if (!canSubmit) return;
    createMutation.mutate(
      { alias: alias.trim(), domain, description: description.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(t('mailbox.toasts.created'));
          onOpenChange(false);
          emitOnboardingEvent('mailbox.created');
        },
        onError: (e) => {
          if (e instanceof ApiError && e.status === 409) {
            toast.error(t('mailbox.toasts.createError'), t('mailbox.create.errors.already_exists'));
          } else {
            toast.error(t('mailbox.toasts.createError'), t('mailbox.toasts.createErrorBody'));
          }
        },
      }
    );
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      className="sm:max-w-lg"
      title={t('mailbox.create.title')}
      description={t('mailbox.create.subtitle')}
    >
      <div className="space-y-3">
        {/* Адаптив (promt-74 §5/§7d, promt-75 §2): на мобилке поле адреса — на отдельной
            строке, домен-селект — на следующей; на десктопе оба в одной строке. */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          {/* Строка 1 (мобилка): поле ввода адреса. */}
          <div className="min-w-0 sm:flex-1">
            <Input
              label={t('mailbox.create.aliasLabel')}
              placeholder={t('mailbox.create.aliasPlaceholder')}
              value={alias}
              onChange={(e) => {
                setAlias(e.target.value);
              }}
              onBlur={() => {
                setTouched(true);
              }}
              onClear={() => {
                setAlias('');
              }}
              error={aliasError}
              autoFocus
            />
          </div>
          {/* Строка 2 (мобилка): выбор домена + кнопка генерации. */}
          <div className="flex min-w-0 gap-2">
            <div className="relative min-w-0 flex-1 sm:w-44 sm:flex-none">
              <select
                value={domain}
                onChange={(e) => {
                  setDomain(e.target.value);
                }}
                aria-label={t('mailbox.create.domainLabel')}
                className="h-[var(--control-h)] w-full appearance-none truncate rounded-[var(--radius-control)] border border-border-strong bg-surface pl-3 pr-9 text-sm text-fg outline-none focus:border-brand pointer-coarse:text-base"
              >
                {domains.map((d) => (
                  <option key={d.name} value={d.name}>
                    @{d.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
            </div>
            <button
              type="button"
              onClick={regenerate}
              aria-label={t('mailbox.create.regenerate')}
              title={t('mailbox.create.regenerate')}
              className="grid h-[var(--control-h)] w-[var(--control-h)] shrink-0 place-items-center rounded-[var(--radius-control)] bg-brand-soft text-brand hover:bg-brand-softhover"
            >
              <RefreshCw className={suggestionsMutation.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </button>
          </div>
        </div>

        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setAlias(s);
                }}
                className="rounded-full bg-page px-3 py-1 text-sm text-fg-muted hover:text-brand"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Подпись поля только во floating-label: placeholder её дублировал (замечание РТК M1). */}
        <Textarea
          label={t('mailbox.create.descriptionPlaceholder')}
          value={description}
          counterMax={DESCRIPTION_MAX}
          error={
            descriptionTooLong
              ? t('mailbox.create.errors.descriptionTooLong', { max: DESCRIPTION_MAX })
              : null
          }
          onChange={(e) => {
            setDescription(e.target.value);
          }}
          onClear={() => {
            setDescription('');
          }}
        />
      </div>

      <ModalActions>
        <Button onClick={onSubmit} disabled={!canSubmit} loading={createMutation.isPending}>
          {t('mailbox.create.submit')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            onOpenChange(false);
          }}
        >
          {t('common.cancel')}
        </Button>
      </ModalActions>
    </Modal>
  );
}
